"use client";

import { useEffect, useState, type CSSProperties } from "react";
import { cn } from "@marketing-sites/utils/cn";
import {
  readStorageItem,
  writeStorageItem,
} from "@marketing-sites/utils/safe-storage";
import { useLocalizeHref } from "@marketing-sites/l10n/providers/localize-href-context";
import { CloseIcon } from "../icons/close-icon";
import { useHeaderContext } from "../context";
import { Text } from "./text";

const bannerVisibilityCacheKey = "sharedBannerClosed";

type BannerProps = {
  linkMetadata: { analyticsId: string };
  closeMetadata: { analyticsId: string };
};

export const Banner = ({ linkMetadata, closeMetadata }: BannerProps) => {
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const { fixtureData } = useHeaderContext();
  // Remember which banner was closed, so swapping in a new one shows it again.
  const bannerId =
    fixtureData?.banner?.analyticsText ?? fixtureData?.banner?.href ?? "";

  useEffect(() => {
    const closedBannerId = readStorageItem("session", bannerVisibilityCacheKey);
    setIsBannerVisible(closedBannerId !== bannerId);
  }, [bannerId]);

  const handleCloseBanner = () => {
    writeStorageItem("session", bannerVisibilityCacheKey, bannerId);

    setIsBannerVisible(false);
  };

  if (!fixtureData?.banner || !isBannerVisible) return null;

  const { theme, backgroundImages } = fixtureData.banner;
  const isLight = theme === "light";

  return (
    <div
      className={cn(
        "top-banner",
        "relative flex items-center justify-center gap-2 overflow-hidden",
        "mb-2",
        "text-center text-sm",
        isLight
          ? "text-off-black bg-[#e1e2e5] [--banner-bg:#e1e2e5]"
          : "bg-off-black text-white [--banner-bg:var(--color-off-black)]",
      )}
    >
      {backgroundImages && (
        <>
          {/* #d9d9d9 matches the mobile artwork's centre so its halves join
              cleanly when the bar is wider than the artwork. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-[#d9d9d9] lg:hidden"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-0 w-1/2 max-w-[195px] bg-[length:auto_100%] bg-left bg-no-repeat min-[740px]:hidden"
            style={{ backgroundImage: `url(${backgroundImages.mobile})` }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-0 w-1/2 max-w-[195px] bg-[length:auto_100%] bg-right bg-no-repeat min-[740px]:hidden"
            style={{ backgroundImage: `url(${backgroundImages.mobile})` }}
          />
          {/* An invisible copy of the full text sizes the gaps either side, so
              the artwork is cropped from its outer edge and never sits under
              the copy. Below lg the desktop artwork would be slivers, so the
              mobile artwork is used, trimmed from its faded inner side. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 hidden min-[740px]:flex"
            style={
              {
                "--banner-art-mobile": `url(${backgroundImages.mobile})`,
                "--banner-art-left": `url(${backgroundImages.left})`,
                "--banner-art-right": `url(${backgroundImages.right})`,
              } as CSSProperties
            }
          >
            <div className="flex-[1_1_0px]">
              <div className="bg-(image:--banner-art-mobile) lg:bg-(image:--banner-art-left) h-full w-full max-w-[195px] bg-[length:auto_100%] bg-left bg-no-repeat [mask-image:linear-gradient(to_left,transparent,black_48px)] lg:max-w-[372px] lg:bg-[position:right_-128px_center] lg:[mask-image:none]" />
            </div>
            <div className="invisible flex items-center gap-2 whitespace-nowrap px-4">
              <Text variant="bodySm">{fixtureData.banner.text}</Text>
              <Text variant="bodySm">{fixtureData.banner.linkLabel}</Text>
            </div>
            <div className="flex flex-[1_1_40px] justify-end">
              <div className="bg-(image:--banner-art-mobile) lg:bg-(image:--banner-art-right) h-full w-full max-w-[195px] bg-[length:auto_100%] bg-right bg-no-repeat [mask-image:linear-gradient(to_right,transparent,black_48px)] lg:max-w-[469px] lg:bg-[position:left_-131px_center] lg:[mask-image:none]" />
            </div>
          </div>
        </>
      )}
      <BannerContent
        onCloseBanner={handleCloseBanner}
        analyticsId={linkMetadata.analyticsId}
      />
      <CloseButton
        onCloseBanner={handleCloseBanner}
        analyticsId={closeMetadata.analyticsId}
      />
    </div>
  );
};

const BannerContent = ({
  onCloseBanner,
  analyticsId,
}: {
  onCloseBanner: () => void;
  analyticsId: string;
}) => {
  const { fixtureData, translatedLabels } = useHeaderContext();
  const localizeHref = useLocalizeHref();
  const href = fixtureData?.banner?.href;
  const localizedHref = href ? localizeHref(href) : href;

  const handleClick = () => {
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("navItemClick", {
          detail: {
            text:
              fixtureData?.banner?.analyticsText ??
              `${fixtureData?.banner?.text} ${fixtureData?.banner?.linkLabel}`,
            destination: localizedHref,
            location: "NavBanner",
            analyticsId,
          },
        }),
      );
    }

    if (!fixtureData?.banner?.disableCloseOnLinkClick) onCloseBanner();
  };

  const banner = fixtureData?.banner;
  // The light banner's full copy fits from 740px, earlier than the default md.
  const isLight = banner?.theme === "light";
  const renderContent = (text?: string) => (
    <>
      <Text variant="bodySm">{text}</Text>
      <Text variant="bodySm" className="pb-0" anchor animateOut>
        {banner?.linkLabel}
      </Text>
    </>
  );

  const mobileContent = renderContent(banner?.textMobile ?? banner?.text);
  const desktopContent = renderContent(banner?.text);

  return (
    <a
      onClick={handleClick}
      href={localizedHref}
      target={banner?.target}
      rel={banner?.target === "_blank" ? "noopener noreferrer" : undefined}
      className="relative w-full py-2"
    >
      {/* Mobile: Marquee (or static when disabled) */}
      {banner?.disableMarquee ? (
        <div
          className={cn(
            "flex w-full items-center justify-center gap-2 text-center",
            isLight ? "min-[740px]:hidden" : "md:hidden",
          )}
        >
          {mobileContent}
        </div>
      ) : (
        <div
          className={cn(
            "relative flex max-w-[92svw] items-center justify-start overflow-hidden",
            isLight ? "min-[740px]:hidden" : "md:hidden",
          )}
        >
          <div className="animate-marquee flex items-center whitespace-nowrap">
            <span className="flex items-center gap-2">{mobileContent}</span>
            <span className="mx-8" aria-hidden="true"></span>
            <span className="flex items-center gap-2">{mobileContent}</span>
          </div>
          {/* Left gradient feather */}
          <div className="bg-linear-to-r from-(--banner-bg) pointer-events-none absolute left-0 top-0 h-full w-8 to-transparent" />
          {/* Right gradient feather */}
          <div className="bg-linear-to-l from-(--banner-bg) pointer-events-none absolute right-0 top-0 h-full w-8 to-transparent" />
        </div>
      )}

      {/* Desktop: Static. Only hold one line where there is room for one —
          translated copy is longer than English and overflows the bar at md. */}
      <div
        className={cn(
          "hidden items-center justify-center gap-2",
          isLight ? "min-[740px]:flex" : "md:flex",
          translatedLabels ? "lg:whitespace-nowrap" : "whitespace-nowrap",
        )}
      >
        {desktopContent}
      </div>
    </a>
  );
};

const CloseButton = ({
  onCloseBanner,
  analyticsId,
}: {
  onCloseBanner: () => void;
  analyticsId: string;
}) => {
  const { labels } = useHeaderContext();

  const handleClick = () => {
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("navItemClick", {
          detail: {
            text: "X",
            destination: "#",
            location: "NavBannerCloseButton",
            analyticsId,
          },
        }),
      );
    }

    onCloseBanner();
  };

  return (
    <button
      onClick={handleClick}
      className={cn(
        "relative h-full p-2 px-2",
        "pointer-events-auto flex cursor-pointer items-center justify-center",
      )}
      aria-label={labels.closeBanner}
    >
      <CloseIcon className="size-4" />
    </button>
  );
};
